import {
  ChevronDownIcon,
  Key01Icon,
  Loading01Icon,
  XIcon,
} from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { Tooltip } from '@/design-system';
import { useTestAlertMutation } from '@/hooks/useSwr';
import {
  AlertActionTarget,
  AlertPagerDutySeverityLevel,
  TestAlertRequest,
} from '@/types/alerts';

import { DisabledTooltip } from '../DisabledTooltip';
import { PopoverDropdownMenu } from '../PopoverDropdownMenu';
import { AlertButtonGroup } from './AlertButtons';
import { WorkspaceSecretDialog } from './WorkspaceSecretDialog';
import { useAlertState } from './useAlertState';
import {
  TEST_ALERT_NOTIFICATION_SUCCESS_MESSAGE,
  buildRequest,
  canUpdateOrCreateAlert,
  getErrorBanner,
} from './utils';

const severityLevels: AlertPagerDutySeverityLevel[] = [
  'critical',
  'error',
  'warning',
  'info',
];

function PagerDutyConfig() {
  const { pagerDutyConfig, setPagerDutyConfig } = useAlertState();
  const [isSecretDialogOpen, setIsSecretDialogOpen] = useState(false);

  const handleSecretSelect = (secretKey: string) => {
    setPagerDutyConfig({ secret_name: secretKey, integration_key: undefined });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-primary">
          Integration Key
        </label>
        <div className="flex items-center rounded-md border border-secondary bg-transparent px-3 py-2 text-sm focus:border-brand">
          {pagerDutyConfig?.secret_name ? (
            <div className="flex flex-1 items-center gap-2">
              <div className="flex items-center rounded-md border border-secondary p-1 pl-2">
                <span>{pagerDutyConfig?.secret_name}</span>
                <button
                  type="button"
                  className="rounded-full p-1 hover:bg-secondary"
                  onClick={() =>
                    setPagerDutyConfig({
                      secret_name: undefined,
                      integration_key: undefined,
                    })
                  }
                >
                  <XIcon className="h-3 w-3 text-tertiary" />
                </button>
              </div>
            </div>
          ) : (
            <input
              className="flex-1 border-none bg-transparent p-0"
              type="text"
              placeholder="Enter key here..."
              onChange={(e) =>
                setPagerDutyConfig({
                  integration_key: e.target.value,
                  secret_name: undefined,
                })
              }
              value={pagerDutyConfig?.integration_key || ''}
            />
          )}
          <Tooltip
            title="Use workspace secret"
            side="top"
            tooltipClassName="text-secondary"
          >
            <button
              type="button"
              className="rounded border border-secondary p-1 hover:bg-secondary"
              onClick={() => setIsSecretDialogOpen(true)}
            >
              <Key01Icon className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
        <p className="text-xs text-quaternary">Required</p>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-primary">Severity</label>
        <PopoverDropdownMenu
          values={severityLevels}
          getTitle={(value) => value}
          onSelect={(value) => setPagerDutyConfig({ severity: value })}
          getIsSelected={(value) => value === pagerDutyConfig?.severity}
          getIsTitleBold={(_) => false}
        >
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-nowrap rounded-md border border-secondary px-3 py-2 text-sm"
          >
            <span>{pagerDutyConfig?.severity || 'Select severity level'}</span>
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        </PopoverDropdownMenu>
        <p className="text-xs text-quaternary">Required</p>
      </div>

      <WorkspaceSecretDialog
        open={isSecretDialogOpen}
        onOpenChange={setIsSecretDialogOpen}
        onSubmit={handleSecretSelect}
      />
    </div>
  );
}

function WebhookConfig() {
  const { webhookConfig, setWebhookConfig } = useAlertState();

  const defaultBody = `{
  "event": "alert_triggered",
  "alert_id": "12345"
}`;

  const defaultHeaders = `{
  "Content-Type": "application/json"
}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-primary">URL</label>
        <input
          type="text"
          placeholder="Enter URL here"
          className="rounded-md border border-secondary bg-transparent px-3 py-2 text-sm focus:border-brand"
          onChange={(e) => setWebhookConfig({ url: e.target.value })}
          value={webhookConfig?.url || ''}
        />
        <p className="text-xs text-quaternary">Required</p>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-primary">Headers</label>
        <textarea
          placeholder={defaultHeaders}
          className="resize-y rounded-md border border-secondary bg-transparent px-3 py-2 text-sm focus:border-brand"
          rows={3}
          onChange={(e) => setWebhookConfig({ headers: e.target.value })}
          value={webhookConfig?.headers}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-primary">Body</label>
        <textarea
          placeholder={defaultBody}
          rows={4}
          className="resize-y rounded-md border border-secondary bg-transparent px-3 py-2 text-sm focus:border-brand"
          onChange={(e) => setWebhookConfig({ body: e.target.value })}
          value={webhookConfig?.body}
        />
      </div>
    </div>
  );
}

export function AlertNotificationOptions({ sessionId }: { sessionId: string }) {
  const alertState = useAlertState();

  const { trigger: testAlert, isMutating: isTestingAlert } =
    useTestAlertMutation(sessionId);

  const handleSendTestAlert = async () => {
    const request = buildRequest(alertState);
    if (request instanceof Error) {
      alertState.setBanner({
        title: "Couldn't send test notification",
        msg: request.message,
        status: 'error',
      });
    } else {
      await testAlert(
        { json: request as TestAlertRequest },
        {
          onSuccess: () => {
            alertState.setBanner({
              title: 'Test notification sent',
              status: 'success',
              msg: TEST_ALERT_NOTIFICATION_SUCCESS_MESSAGE,
            });
          },
          onError: (err) => {
            alertState.setBanner(
              getErrorBanner("Couldn't send test notification", err)
            );
          },
        }
      );
    }
  };

  const isTestAlertDisabled = !canUpdateOrCreateAlert(alertState);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-md font-medium">Notification Settings</h3>
        <p className="text-sm text-tertiary">
          Choose how your team gets alerted. Learn more in our docs{' '}
          <a
            target="_blank"
            className="underline"
            href="https://docs.langchain.com/langsmith/alerts"
          >
            here
          </a>
          .
        </p>
      </div>
      <div className="flex flex-col gap-4 rounded-lg border border-secondary p-4">
        <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-2">
          <AlertButtonGroup<AlertActionTarget>
            options={[
              { value: AlertActionTarget.pagerduty, label: 'PagerDuty' },
              { value: AlertActionTarget.webhook, label: 'Webhook' },
            ]}
            value={alertState.actionTarget}
            onChange={(target) => alertState.setActionTarget(target)}
          />
          <DisabledTooltip
            disabled={isTestAlertDisabled}
            disabledMsg="Please complete all fields to send a test notification."
          >
            <button
              type="button"
              className="disabled:no-pointer-events flex items-center gap-1 text-sm text-secondary underline disabled:no-underline disabled:opacity-50"
              disabled={isTestAlertDisabled}
              onClick={handleSendTestAlert}
            >
              {isTestingAlert ? (
                <>
                  <Loading01Icon className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Sending...</span>
                </>
              ) : (
                <span className="text-sm">Send Test Notification</span>
              )}
            </button>
          </DisabledTooltip>
        </div>
        {alertState.actionTarget === AlertActionTarget.pagerduty && (
          <PagerDutyConfig />
        )}
        {alertState.actionTarget === AlertActionTarget.webhook && (
          <WebhookConfig />
        )}
      </div>
    </div>
  );
}
